import { useEffect, useRef, useState } from 'react';
import { buildAnalyticsContext, getSessionId } from '../utility/analyticsHelper';
import { postData } from '../apiService';
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// ✅ Session expiry — 30 minutes (same as Google Analytics)
const SESSION_EXPIRY_MS = 30 * 60 * 1000;

// ✅ Module-level: prevents duplicate API calls during same page lifecycle
const recordedViews = new Set();

// ✅ Safe cookie helper — works even when storage is blocked
const cookie = {
  get: (key) => {
    try {
      const match = document.cookie.match(new RegExp('(^| )' + key + '=([^;]+)'));
      return match ? decodeURIComponent(match[2]) : null;
    } catch { return null; }
  },
  set: (key, value, minutes) => {
    try {
      const expires = new Date(Date.now() + minutes * 60 * 1000).toUTCString();
      document.cookie = `${key}=${encodeURIComponent(value)};expires=${expires};path=/`;
    } catch { }
  }
};

export const usePageViewTracking = (masterPageId, childPageId) => {

  const navigate = useNavigate();
  const location = useLocation();
  const [pageViewCount, setPageViewCount] = useState(0);
  const pageViewRecorded = useRef(false);
  const pageLoadTime = useRef(Date.now());

  useEffect(() => {
    // ✅ Wait until valid IDs are available
    if (!masterPageId || masterPageId <= 0) return;
    //if (!childPageId || childPageId <= 0) return;

    const viewKey = `pv_${masterPageId}_${childPageId}`;



    // ✅ Rule 2: Check cookie — was this page visited within last 30 min?
    const lastVisit = cookie.get(viewKey);
    const now = Date.now();

    if (lastVisit && (now - parseInt(lastVisit)) < SESSION_EXPIRY_MS) {
      recordedViews.add(viewKey);
      // Same session — don't count again
      // But still try to load existing count from cookie
      const savedCount = cookie.get(`${viewKey}_count`);
      if (savedCount) setPageViewCount(parseInt(savedCount));
      return;
    }

    // ✅ Rule 1: Already recorded in this component lifecycle → skip
    // Only after cookie check, guard with the Set
    if (recordedViews.has(viewKey)) return;
    recordedViews.add(viewKey);

    const recordPageView = async () => {

      // ✅ Mark immediately to prevent race conditions
      recordedViews.add(viewKey);

      try {
        const analyticsData = buildAnalyticsContext(masterPageId, childPageId);
        const response = await postData(`PageView/RecordPageView`, analyticsData, navigate);
        if (response > 0) {
          console.log("Page Viewsss", response);
          setPageViewCount(response);
          pageViewRecorded.current = true;
          pageLoadTime.current = Date.now();

          // ✅ Save to cookie — expires in 30 min
          cookie.set(viewKey, now.toString(), 30);
          cookie.set(`${viewKey}_count`, response.toString(), 30);
        }
      } catch (err) {
        console.error('Page view tracking error:', err);
        recordedViews.delete(viewKey); // allow retry
      }
    };

    recordPageView();

    // ✅ Track time spent on page
    const sendTimeOnPage = () => {
      if (!pageViewRecorded.current) return;
      const timeOnPage = Math.floor((Date.now() - pageLoadTime.current) / 1000);
      if (timeOnPage < 1) return;

      const analyticsData = buildAnalyticsContext(masterPageId, childPageId);
      analyticsData.timeOnPage = timeOnPage;
      const blob = new Blob([JSON.stringify(analyticsData)], { type: 'application/json' });
      navigator.sendBeacon(`${API_BASE_URL}PageView/UpdateTimeOnPage`, blob);
    };

    window.addEventListener('beforeunload', sendTimeOnPage);
    window.addEventListener('pagehide', sendTimeOnPage);

    return () => {
      sendTimeOnPage();
      window.removeEventListener('beforeunload', sendTimeOnPage);
      window.removeEventListener('pagehide', sendTimeOnPage);
    };

  }, [masterPageId, childPageId]);

  return { pageViewCount };
};